import { usePerfectGiftBoxContext } from '@/src/common/contexts/PerfectGiftBoxContext'
import Card from '@/src/common/components/marketing/cards/Card'
import { styled } from '@/src/stitches.config'
import Image from 'next/image'
import { useState } from 'react'
import { GiftBox } from '@/src/modules/gift-box/content/StyledComponents'

const OnlyCardWrapper = styled('div', {
    display: 'flex',
    jc: 'center',
    ai: 'center',
    height: 'fit-content',
    pb: 20,
    width: '100%',
    maxWidth: 500,
    '@sm': {
        height: 365.99,
    },
})

const Wrapper = styled('div', {})
const BoxesWrapper = styled('div', {
    display: 'flex',
    ai: 'center',
    jc: 'center',
    width: '100%',
    maxWidth: 500,
    height: 55,
    gap: '5%',
    '.gift-box-wrapper': {
        pb: 0,
    },
    '.box-active': {
        border: '2px solid $primary',
    },
})
const Box = styled('div', {
    display: 'flex',
    ai: 'center',
    jc: 'center',
    height: '100%',
    maxWidth: 70,
    cursor: 'pointer',
    br: 5,
    p: 10,
    border: '2px solid $mediumGray',
})

const StandardBoxImage = ({
    priority = false,
    showCard = true,
    showBox = true,
    showCustomFields = true,
    borderRadius = 12,
}: {
    priority?: boolean
    showCard?: boolean
    showBox?: boolean
    showCustomFields?: boolean
    borderRadius?: number | string
}) => {
    const { messageOnCard, nameOnCard, designName, designImage, defaultImage } =
        usePerfectGiftBoxContext()
    const image = designImage ? designImage : defaultImage

    if (!showBox) {
        return (
            <OnlyCardWrapper>
                <Card
                    size={'lg'}
                    image={image}
                    overlayType={'full'}
                    isExtended={false}
                    message={messageOnCard ? messageOnCard : 'Custom message'}
                    name={nameOnCard ? nameOnCard : 'Recipient name'}
                    showLoader={!designName}
                    priority={priority}
                    showCustomFields={showCustomFields}
                    borderRadius={borderRadius}
                />
            </OnlyCardWrapper>
        )
    }

    return (
        <GiftBox className={'gift-box-wrapper'}>
            <Image
                src={'/images/gift-box-image-v2.png'}
                alt={'PerfectGift Box Image'}
                width={640}
                height={438}
                className={'gift-box-image'}
                priority={priority}
            />
            {showCard && (
                <Card
                    size={'card-for-box'}
                    image={image}
                    overlayType={'full'}
                    isExtended={false}
                    message={messageOnCard ? messageOnCard : 'Custom message'}
                    name={nameOnCard ? nameOnCard : 'Recipient name'}
                    showLoader={!designName}
                    priority={priority}
                    showCustomFields={showCustomFields}
                    borderRadius={borderRadius}
                />
            )}
        </GiftBox>
    )
}

const SmallBoxImageForPreview = ({
    id,
    currentlyActive,
    setCurrentlyActive,
    showBox,
    showCard,
}: {
    id: string
    currentlyActive: string
    setCurrentlyActive: Function
    showCard: boolean
    showBox: boolean
}) => {
    const { messageOnCard, nameOnCard, designName, designImage, defaultImage } =
        usePerfectGiftBoxContext()
    const image = designImage ? designImage : defaultImage
    const isActive: boolean = currentlyActive === id

    if (!showBox) {
        return (
            <Box
                className={isActive ? 'box-active' : ''}
                onClick={() => setCurrentlyActive(id)}
            >
                <Card
                    size={'sm'}
                    image={image}
                    overlayType={'full'}
                    isExtended={false}
                    message={messageOnCard ? messageOnCard : 'Custom message'}
                    name={nameOnCard ? nameOnCard : 'Recipient name'}
                    showLoader={!designName}
                    showCustomFields={false}
                    borderRadius={3}
                />
            </Box>
        )
    }

    return (
        <Box
            className={isActive ? 'box-active' : ''}
            onClick={() => setCurrentlyActive(id)}
        >
            <StandardBoxImage
                showCard={showCard}
                showCustomFields={false}
                borderRadius={3}
            />
        </Box>
    )
}

const GiftBoxWithCard = ({
    priority,
    showPreviewBoxes = false,
}: {
    priority?: boolean
    showPreviewBoxes?: boolean
}) => {
    const [currentlyActive, setCurrentlyActive] = useState<
        'box-with-card' | 'only-box' | 'only-card'
    >('box-with-card')

    if (!showPreviewBoxes) {
        return <StandardBoxImage priority={priority} />
    }

    return (
        <Wrapper>
            <StandardBoxImage
                showCard={currentlyActive !== 'only-box'}
                showBox={currentlyActive !== 'only-card'}
                priority={priority}
            />
            <BoxesWrapper>
                <SmallBoxImageForPreview
                    showCard={true}
                    showBox={true}
                    currentlyActive={currentlyActive}
                    setCurrentlyActive={setCurrentlyActive}
                    id={'box-with-card'}
                />
                <SmallBoxImageForPreview
                    showCard={false}
                    showBox={true}
                    currentlyActive={currentlyActive}
                    setCurrentlyActive={setCurrentlyActive}
                    id={'only-box'}
                />
                <SmallBoxImageForPreview
                    showCard={true}
                    showBox={false}
                    currentlyActive={currentlyActive}
                    setCurrentlyActive={setCurrentlyActive}
                    id={'only-card'}
                />
            </BoxesWrapper>
        </Wrapper>
    )
}

export default GiftBoxWithCard
